import { APIPropTypes, PropTypes } from 'typed/imports';
import { ConnectedProps } from 'typed/components/utils';
import { Resource } from 'typed/components/common';
import { RequestStatus, TrackingLevel, entities } from 'typed/api';
import * as actions from 'typed/actions';
import { State } from 'store';

/* eslint-disable-next-line */
const mappirize = <T>(dsMap: T): T => {
    Object.freeze(dsMap);

    if (DEBUG && (window as any).Proxy) {
        return new Proxy(dsMap, {
            get: (target: any, name: any) => {
                if (target[name] === undefined) {
                    throw new Error(`Map has no member ${name}.`);
                }
                return target[name];
            }
        });
    }

    return dsMap;
};

export interface CorporateStructureItemProps {
    products: entities.NetworkArray<entities.Product>;
    geoAreas: entities.NetworkArray<entities.GeoArea>;
    customers: entities.NetworkArray<entities.Customer>;
    trackingLevel1Items: entities.NetworkArray<entities.TrackingLevel1Item>;
    trackingLevel2Items: entities.NetworkArray<entities.TrackingLevel2Item>;
    trackingLevel3Items: entities.NetworkArray<entities.TrackingLevel3Item>;
    trackingLevel4Items: entities.NetworkArray<entities.TrackingLevel4Item>;
    companyLegalEntities: entities.NetworkArray<entities.CompanyLegalEntity>;
    companyFunctions: entities.CompanyFunctionNetworkArray;
}

export interface CorporateStructureProps extends CorporateStructureItemProps {
    trackingLevels: entities.NetworkArray<entities.TrackingLevel>;
}

export const corporateStructurePropTypes = {
    trackingLevels: APIPropTypes.networkArrayOf(APIPropTypes.TrackingLevel).isRequired,
    products: APIPropTypes.networkArrayOf(APIPropTypes.Product).isRequired,
    geoAreas: APIPropTypes.networkArrayOf(APIPropTypes.GeoArea).isRequired,
    customers: APIPropTypes.networkArrayOf(APIPropTypes.Customer).isRequired,
    trackingLevel1Items: APIPropTypes.networkArrayOf(APIPropTypes.TrackingLevel1Item).isRequired,
    trackingLevel2Items: APIPropTypes.networkArrayOf(APIPropTypes.TrackingLevel2Item).isRequired,
    trackingLevel3Items: APIPropTypes.networkArrayOf(APIPropTypes.TrackingLevel3Item).isRequired,
    trackingLevel4Items: APIPropTypes.networkArrayOf(APIPropTypes.TrackingLevel4Item).isRequired,
    companyLegalEntities: APIPropTypes.networkArrayOf(APIPropTypes.CompanyLegalEntity).isRequired,
    companyFunctions: APIPropTypes.networkArrayOf(APIPropTypes.CompanyFunction).isRequired
};

export function corporateStructureMapStateToProps(state: State, _ownProps?: unknown): CorporateStructureProps {
    return state.corporateStructure;
}

type CorporateStructureMap<TValue> = { [P in TrackingLevel]: TValue };

export const CORPORATE_STRUCTURE_ENTITIES_MAP: CorporateStructureMap<string> = mappirize({
    [TrackingLevel.PRODUCT]: 'Product',
    [TrackingLevel.GEO_AREA]: 'GeoArea',
    [TrackingLevel.CUSTOMER]: 'Customer',
    [TrackingLevel.TRACKING_LEVEL_1]: 'TrackingLevel1Item',
    [TrackingLevel.TRACKING_LEVEL_2]: 'TrackingLevel2Item',
    [TrackingLevel.TRACKING_LEVEL_3]: 'TrackingLevel3Item',
    [TrackingLevel.TRACKING_LEVEL_4]: 'TrackingLevel4Item',
    [TrackingLevel.COMPANY_LEGAL_ENTITY]: 'CompanyLegalEntity',
    [TrackingLevel.COMPANY_FUNCTION]: 'CompanyFunction'
});


export const CORPORATE_STRUCTURE_STORE_KEYS_MAP: CorporateStructureMap<keyof CorporateStructureItemProps> = mappirize({
    [TrackingLevel.PRODUCT]: 'products',
    [TrackingLevel.GEO_AREA]: 'geoAreas',
    [TrackingLevel.CUSTOMER]: 'customers',
    [TrackingLevel.TRACKING_LEVEL_1]: 'trackingLevel1Items',
    [TrackingLevel.TRACKING_LEVEL_2]: 'trackingLevel2Items',
    [TrackingLevel.TRACKING_LEVEL_3]: 'trackingLevel3Items',
    [TrackingLevel.TRACKING_LEVEL_4]: 'trackingLevel4Items',
    [TrackingLevel.COMPANY_LEGAL_ENTITY]: 'companyLegalEntities',
    [TrackingLevel.COMPANY_FUNCTION]: 'companyFunctions'
});

export const CORPORATE_STRUCTURE_LINK_PROPERTIES_MAP: CorporateStructureMap<keyof entities.CorporateStructureLink> = mappirize({
    [TrackingLevel.PRODUCT]: 'ProductID',
    [TrackingLevel.GEO_AREA]: 'GeoAreaID',
    [TrackingLevel.CUSTOMER]: 'CustomerID',
    [TrackingLevel.TRACKING_LEVEL_1]: 'TrackingLevel1ItemID',
    [TrackingLevel.TRACKING_LEVEL_2]: 'TrackingLevel2ItemID',
    [TrackingLevel.TRACKING_LEVEL_3]: 'TrackingLevel3ItemID',
    [TrackingLevel.TRACKING_LEVEL_4]: 'TrackingLevel4ItemID',
    [TrackingLevel.COMPANY_LEGAL_ENTITY]: 'CompanyLegalEntityID',
    [TrackingLevel.COMPANY_FUNCTION]: 'CompanyFunctionID'
});

export const CORPORATE_STRUCTURE_FILTER_PROPERTIES_MAP: CorporateStructureMap<keyof entities.CorporateStructureFilter> = mappirize({
    [TrackingLevel.PRODUCT]: 'ProductIDs',
    [TrackingLevel.GEO_AREA]: 'GeoAreaIDs',
    [TrackingLevel.CUSTOMER]: 'CustomerIDs',
    [TrackingLevel.TRACKING_LEVEL_1]: 'TrackingLevel1ItemIDs',
    [TrackingLevel.TRACKING_LEVEL_2]: 'TrackingLevel2ItemIDs',
    [TrackingLevel.TRACKING_LEVEL_3]: 'TrackingLevel3ItemIDs',
    [TrackingLevel.TRACKING_LEVEL_4]: 'TrackingLevel4ItemIDs',
    [TrackingLevel.COMPANY_LEGAL_ENTITY]: 'CompanyLegalEntityIDs',
    [TrackingLevel.COMPANY_FUNCTION]: 'CompanyFunctionIDs'
});

export const CORPORATE_STRUCTURE_INDICATOR_COLLECT_PROPERTIES_MAP: CorporateStructureMap<string> = mappirize({
    [TrackingLevel.PRODUCT]: 'CollectProduct',
    [TrackingLevel.GEO_AREA]: 'CollectGeoArea',
    [TrackingLevel.CUSTOMER]: 'CollectCustomer',
    [TrackingLevel.TRACKING_LEVEL_1]: 'CollectTrackingLevel1Item',
    [TrackingLevel.TRACKING_LEVEL_2]: 'CollectTrackingLevel2Item',
    [TrackingLevel.TRACKING_LEVEL_3]: 'CollectTrackingLevel3Item',
    [TrackingLevel.TRACKING_LEVEL_4]: 'CollectTrackingLevel4Item',
    [TrackingLevel.COMPANY_LEGAL_ENTITY]: 'CollectLegalUnit',
    [TrackingLevel.COMPANY_FUNCTION]: 'CollectFunction'
});


export const CORPORATE_STRUCTURE_DASHBOARD_FILTERING_ENABLEDNESS_PROPERTIES_MAP: CorporateStructureMap<keyof entities.DashboardPageReport> = mappirize({
    [TrackingLevel.PRODUCT]: 'ProductFilteringEnabled',
    [TrackingLevel.GEO_AREA]: 'GeoAreaFilteringEnabled',
    [TrackingLevel.CUSTOMER]: 'CustomerFilteringEnabled',
    [TrackingLevel.TRACKING_LEVEL_1]: 'TrackingLevel1FilteringEnabled',
    [TrackingLevel.TRACKING_LEVEL_2]: 'TrackingLevel2FilteringEnabled',
    [TrackingLevel.TRACKING_LEVEL_3]: 'TrackingLevel3FilteringEnabled',
    [TrackingLevel.TRACKING_LEVEL_4]: 'TrackingLevel4FilteringEnabled',
    [TrackingLevel.COMPANY_LEGAL_ENTITY]: 'CompanyLegalEntityFilteringEnabled',
    [TrackingLevel.COMPANY_FUNCTION]: 'CompanyFunctionFilteringEnabled'
});


export function getCorporateStructureItemName(levelID: TrackingLevel, item: entities.TrackingLevelItemBase): Resource {
    switch (levelID) {
        case TrackingLevel.PRODUCT:
        case TrackingLevel.GEO_AREA:
        case TrackingLevel.CUSTOMER:
            return item.Name || { id: 'corporate_structure.untitled_item' };
        case TrackingLevel.TRACKING_LEVEL_1:
        case TrackingLevel.TRACKING_LEVEL_2:
        case TrackingLevel.TRACKING_LEVEL_3:
        case TrackingLevel.TRACKING_LEVEL_4:
            return item.Name || { id: 'corporate_structure.untitled_item' };
        case TrackingLevel.COMPANY_LEGAL_ENTITY: return item.Name || { id: 'organization_structure.untitled_legal_entity' };
        case TrackingLevel.COMPANY_FUNCTION: return item.Name || { id: 'organization_structure.untitled_function' };
        default: throw new Error(`Unknown tracking level ${levelID}`);
    }
}

export function getCorporateStructureLinkDescription(props: CorporateStructureItemProps, link: entities.CorporateStructureLink, skippedLevelID?: TrackingLevel): Resource;
export function getCorporateStructureLinkDescription(props: CorporateStructureItemProps, link: entities.CorporateStructureLink, skippedLevelID?: TrackingLevel, formatMessage?: any, separator?: string): string;
export function getCorporateStructureLinkDescription(props: CorporateStructureItemProps, link: entities.CorporateStructureLink, skippedLevelID?: TrackingLevel, formatMessage?: any, separator = ', '): Resource {
    if ((Object.values(TrackingLevel) as TrackingLevel[]).filter(value => typeof value === 'number').some(levelID =>
        !!link[CORPORATE_STRUCTURE_LINK_PROPERTIES_MAP[levelID]] &&
        props[CORPORATE_STRUCTURE_STORE_KEYS_MAP[levelID]].Custom.status !== RequestStatus.SUCCEEDED
    )) {
        return '';
    }

    return (Object.values(TrackingLevel) as TrackingLevel[]).filter(value => typeof value === 'number')
        .filter(levelID => skippedLevelID !== levelID && link[CORPORATE_STRUCTURE_LINK_PROPERTIES_MAP[levelID]])
        .map(levelID => getCorporateStructureItemName(levelID, props[CORPORATE_STRUCTURE_STORE_KEYS_MAP[levelID]].Data[props[CORPORATE_STRUCTURE_STORE_KEYS_MAP[levelID]].Data.getIndexByID(link[CORPORATE_STRUCTURE_LINK_PROPERTIES_MAP[levelID]] as number)!]))
        .map(name => typeof name === 'string' ? name : formatMessage ? formatMessage(name) : '')
        .join(separator);
}

export function requestCorporateStructureData(props: CorporateStructureProps & ConnectedProps): void;
export function requestCorporateStructureData(props: CorporateStructureProps & ConnectedProps, neededLevelIDs: TrackingLevel[]): void;
export function requestCorporateStructureData(props: CorporateStructureProps & ConnectedProps, neededLevelIDs?: TrackingLevel[]): void {
    if (!neededLevelIDs || neededLevelIDs.length) {
        if (props.trackingLevels.Custom.status === RequestStatus.UNSYNCED) {
            props.dispatch(actions.trackingLevels.loadAll());
        }
    }

    if (!neededLevelIDs || neededLevelIDs.includes(TrackingLevel.PRODUCT)) {
        if (props.products.Custom.status === RequestStatus.UNSYNCED) {
            props.dispatch(actions.products.loadAll());
        }
    }

    if (!neededLevelIDs || neededLevelIDs.includes(TrackingLevel.GEO_AREA)) {
        if (props.geoAreas.Custom.status === RequestStatus.UNSYNCED) {
            props.dispatch(actions.geoAreas.loadAll());
        }
    }


    if (!neededLevelIDs || neededLevelIDs.includes(TrackingLevel.CUSTOMER)) {
        if (props.customers.Custom.status === RequestStatus.UNSYNCED) {
            props.dispatch(actions.customers.loadAll());
        }
    }

    if (!neededLevelIDs || neededLevelIDs.includes(TrackingLevel.TRACKING_LEVEL_1)) {
        if (props.trackingLevel1Items.Custom.status === RequestStatus.UNSYNCED) {
            props.dispatch(actions.trackingLevel1Items.loadAll());
        }
    }

    if (!neededLevelIDs || neededLevelIDs.includes(TrackingLevel.TRACKING_LEVEL_2)) {
        if (props.trackingLevel2Items.Custom.status === RequestStatus.UNSYNCED) {
            props.dispatch(actions.trackingLevel2Items.loadAll());
        }
    }

    if (!neededLevelIDs || neededLevelIDs.includes(TrackingLevel.TRACKING_LEVEL_3)) {
        if (props.trackingLevel3Items.Custom.status === RequestStatus.UNSYNCED) {
            props.dispatch(actions.trackingLevel3Items.loadAll());
        }
    }

    if (!neededLevelIDs || neededLevelIDs.includes(TrackingLevel.TRACKING_LEVEL_4)) {
        if (props.trackingLevel4Items.Custom.status === RequestStatus.UNSYNCED) {
            props.dispatch(actions.trackingLevel4Items.loadAll());
        }
    }

    if (!neededLevelIDs || neededLevelIDs.includes(TrackingLevel.COMPANY_LEGAL_ENTITY)) {
        if (props.companyLegalEntities.Custom.status === RequestStatus.UNSYNCED) {
            props.dispatch(actions.companyLegalEntities.loadAll());
        }
    }

    if (!neededLevelIDs || neededLevelIDs.includes(TrackingLevel.COMPANY_FUNCTION)) {
        if (props.companyFunctions.Custom.status === RequestStatus.UNSYNCED) {
            props.dispatch(actions.companyFunctions.loadAll());
        }
    }
}

export function isAllCorporateStructureDataLoaded(props: CorporateStructureProps): boolean;
export function isAllCorporateStructureDataLoaded(props: CorporateStructureProps, neededLevelIDs: TrackingLevel[]): boolean;
export function isAllCorporateStructureDataLoaded(props: CorporateStructureProps, neededLevelIDs?: TrackingLevel[]): boolean {
    return true &&
        (neededLevelIDs && !neededLevelIDs.length || props.trackingLevels.Custom.status === RequestStatus.SUCCEEDED) &&
        (neededLevelIDs && !neededLevelIDs.includes(TrackingLevel.PRODUCT) || props.products.Custom.status === RequestStatus.SUCCEEDED) &&
        (neededLevelIDs && !neededLevelIDs.includes(TrackingLevel.GEO_AREA) || props.geoAreas.Custom.status === RequestStatus.SUCCEEDED) &&
        (neededLevelIDs && !neededLevelIDs.includes(TrackingLevel.CUSTOMER) || props.customers.Custom.status === RequestStatus.SUCCEEDED) &&
        (neededLevelIDs && !neededLevelIDs.includes(TrackingLevel.TRACKING_LEVEL_1) || props.trackingLevel1Items.Custom.status === RequestStatus.SUCCEEDED) &&
        (neededLevelIDs && !neededLevelIDs.includes(TrackingLevel.TRACKING_LEVEL_2) || props.trackingLevel2Items.Custom.status === RequestStatus.SUCCEEDED) &&
        (neededLevelIDs && !neededLevelIDs.includes(TrackingLevel.TRACKING_LEVEL_3) || props.trackingLevel3Items.Custom.status === RequestStatus.SUCCEEDED) &&
        (neededLevelIDs && !neededLevelIDs.includes(TrackingLevel.TRACKING_LEVEL_4) || props.trackingLevel4Items.Custom.status === RequestStatus.SUCCEEDED) &&
        (neededLevelIDs && !neededLevelIDs.includes(TrackingLevel.COMPANY_LEGAL_ENTITY) || props.companyLegalEntities.Custom.status === RequestStatus.SUCCEEDED) &&
        (neededLevelIDs && !neededLevelIDs.includes(TrackingLevel.COMPANY_FUNCTION) || props.companyFunctions.Custom.status === RequestStatus.SUCCEEDED);
}

export function corporateStructureLinksEqual(left: entities.CorporateStructureLink, right: entities.CorporateStructureLink): boolean {
    return Object.values(CORPORATE_STRUCTURE_LINK_PROPERTIES_MAP).every(prop => left[prop] === right[prop]);
}


export const EXTERNAL_LEVEL_IDS: TrackingLevel[] = [
    TrackingLevel.PRODUCT,
    TrackingLevel.GEO_AREA,
    TrackingLevel.CUSTOMER,
    TrackingLevel.TRACKING_LEVEL_1,
    TrackingLevel.TRACKING_LEVEL_2,
    TrackingLevel.TRACKING_LEVEL_3,
    TrackingLevel.TRACKING_LEVEL_4
];

export const INTERNAL_LEVEL_IDS: TrackingLevel[] = [
    TrackingLevel.COMPANY_LEGAL_ENTITY,
    TrackingLevel.COMPANY_FUNCTION
];

export enum TrackingLevelGroup {
    ALL_LEVELS = 'ALL_LEVELS',
    EXTERNAL_LEVELS = 'EXTERNAL_LEVELS',
    INTERNAL_LEVELS = 'INTERNAL_LEVELS',
    CUSTOM_LEVELS = 'CUSTOM_LEVELS',
    PAGELESS_LEVELS = 'PAGELESS_LEVELS'
}

export const trackingLevelPropType = PropTypes.oneOf([
    TrackingLevel.PRODUCT,
    TrackingLevel.GEO_AREA,
    TrackingLevel.CUSTOMER,
    TrackingLevel.TRACKING_LEVEL_1,
    TrackingLevel.TRACKING_LEVEL_2,
    TrackingLevel.TRACKING_LEVEL_3,
    TrackingLevel.TRACKING_LEVEL_4,
    TrackingLevel.COMPANY_LEGAL_ENTITY,
    TrackingLevel.COMPANY_FUNCTION
]);

export const trackingLevelGroupPropType = PropTypes.oneOf([TrackingLevelGroup.ALL_LEVELS, TrackingLevelGroup.EXTERNAL_LEVELS, TrackingLevelGroup.INTERNAL_LEVELS, TrackingLevelGroup.CUSTOM_LEVELS, TrackingLevelGroup.PAGELESS_LEVELS]);

// TODO make functions Nestable so they are sorted automatically
export function sortFunctions(functions: entities.CompanyFunction[]) {
    const result = [];

    functions = functions.copy().sortBy(({ Name }) => Name);

    const sortQueue = functions.filter(({ FatherID }) => !FatherID);

    while (sortQueue.length) {
        const companyFunction = sortQueue.first(),
            children = functions.filter(({ FatherID }) => FatherID === companyFunction.ID);

        sortQueue.splice(0, 1, ...children);
        result.push(companyFunction);
    }

    return result;
}
